import { useState } from 'react';

import { Backdrop, Box, Paper, Popper, Typography, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Email, Person, Phone } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import {
  useHigherEdOrgManagerQuery, useHigherEdUserProfileQuery, useReferralByStudentIdQuery,
  useStudentContactsQuery
} from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { CollapsableText } from '@uplift/ui-v2';
import { getCareTypeLabel } from '@uplift/utils';

import { ReferralCellProps } from './cells';

export type ReferralPopoverProps = ReferralCellProps['row'] & {
  onClose: () => void;
  anchorEl?: HTMLElement;
};

const ARROW_SIZE = 12;

const Arrow = () => {
  return (
    <Box
      data-id="arrow"
      width={ARROW_SIZE}
      height={ARROW_SIZE}
      bgcolor="white"
      position="absolute"
      marginLeft={`${ARROW_SIZE}px`}
      style={{ transform: 'translateX(-50%) rotateZ(45deg)' }}
      zIndex={1}
    />
  );
};

export const ReferralPopover: React.VFC<ReferralPopoverProps> = ({ student, onClose, anchorEl }) => {
  const [ noteHeight, setNoteHeight ] = useState(undefined);
  const { classes, cx } = useReferralPopoverStyles();
  const theme = useTheme();

  const { data: referralData } = useReferralByStudentIdQuery(student.id);
  const referral = referralData?.[0];

  const { data: contactsData } = useStudentContactsQuery(student.id);
  const phoneNumber = contactsData?.[0]?.value;

  const me = useCurrentuser();
  const managerId = referral?.fromUserId;

  const { data: managerData, isFetched: managerDataFetched } = useHigherEdOrgManagerQuery(managerId, { enabled: !!managerId });
  const { data: managerProfileData, isFetched: managerProfileFetched } = useHigherEdUserProfileQuery(managerId, { enabled: !!managerId });

  const suffixes = managerProfileData?.suffixes.join(', ');
  const managerName = `${managerProfileData?.prefix ?? ''} ${managerData?.user?.firstName} ${managerData?.user?.lastName}${suffixes && `, ${suffixes}`}`;
  const isSentByMe = me?.id === managerId;
  const manager = isSentByMe ? 'You' : managerName;
  const createdAt = CalendarDateService.date(referral?.createdAt).format('MM/DD/YYYY, h:mm A');

  return (managerDataFetched && managerProfileFetched && (
    <>
      <Backdrop open invisible style={{ zIndex: theme.zIndex.modal, position: 'absolute' }} onClick={onClose} />
      <Popper
        open
        placement="bottom-start"
        className={classes.popper}
        anchorEl={anchorEl}
        modifiers={[
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: '-16px, -0px',
            },
          },
          {
            name: 'flip',
            enabled: true,
          },
          {
            name: 'preventOverflow',
            enabled: false,
            options: {
              height: noteHeight,
            }
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: '[data-id="arrow"]',
            }
          },
        ]}
      >
        <Box component={Paper} position="relative" marginTop="10px" marginBottom="10px">
          <Arrow />
          <Box className={classes.container}>
            <Box p={3} pb={1}>
              <Typography variant="h6" className={classes.header} data-testid="referral-popover-title">
                {`Referral for ${getCareTypeLabel(referral?.careType)}`}
              </Typography>
              <Box className={classes.info}>
                <Box className={classes.student}>
                  <Person color="secondary" className={classes.icon} />
                  <Typography variant="body2" className={classes.label} data-testid="referral-popover-student-name">
                    {student?.fullName}
                  </Typography>
                </Box>
                <Box className={classes.student}>
                  <Email color="secondary" className={classes.icon} />
                  <Typography variant="body2" className={classes.label} data-testid="referral-popover-student-email">
                    {student?.email}
                  </Typography>
                </Box>
                <Box className={classes.student}>
                  <Phone color="secondary" className={classes.icon} />
                  <Typography
                    variant="body2"
                    className={classes.label}
                    data-testid="referral-popover-student-phone-number"
                  >
                    {phoneNumber}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className={classes.noteContainer} data-testid="referral-popover-note-container">
              <Box className={classes.noteBackground}>
                <Typography variant="body2">Note to Provider:</Typography>
                {referral?.content ? (
                  <CollapsableText
                    previewMaxLength={150}
                    maxHeight="50vh"
                    onCollapse={(open) => setNoteHeight(open)}
                    data-testid="referral-popover-note-content"
                  >
                    {referral.content}
                  </CollapsableText>
                ) : (
                  <Typography
                    variant="body2"
                    className={cx(classes.label, classes.italic)}
                    data-testid="referral-popover-note-content"
                  >
                    None
                  </Typography>
                )}
              </Box>
              <Box p={2} pl={3}>
                <Typography
                  variant="body2"
                  data-testid="referral-popover-sent-by"
                >{`Sent by ${manager} ${createdAt}`}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Popper>
    </>
  ));
};

const useReferralPopoverStyles = makeStyles({ name: 'ReferralPopover' })((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '388px',
  },
  header: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.5px',
    paddingBottom: theme.spacing(1.5),
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  student: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  label: {
    fontWeight: 400,
    paragraph: '14px',
    letterSpacing: '0.4px',
    color: theme.palette.text.primary,
  },
  italic: {
    fontStyle: 'italic',
  },
  icon: {
    transform: 'scale(0.75)',
  },
  noteContainer: {
    backgroundColor: theme.palette.grey[50],
  },
  noteBackground: {
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(3, 3, 0, 3),
    backgroundColor: theme.palette.background.paper,
  },
  popper: {
    zIndex: theme.zIndex.modal + 1,
    "&[x-placement*='bottom'] [data-id='arrow']": {
      top: `-${ARROW_SIZE / 2}px`,
      backgroundColor: 'white',
    },
    "&[x-placement*='top'] [data-id='arrow']": {
      bottom: `-${ARROW_SIZE / 2 + 1}px`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: '1px solid #ccc',
      borderRight: '1px solid #ddd',
    },
  },
}));
